import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Add,
  ExpandMore,
  ExpandLess,
  LocationOn,
  Business,
  FactoryOutlined,
  Router,
  Storage,
  Restaurant,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { AppDispatch, RootState } from '../../../store/store';
import { createLocation, Location } from '../../../store/slices/masterDataSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LOCATION_TYPES: Location['type'][] = [
  'site',
  'kitchen',
  'building',
  'floor',
  'zone',
  'aisle',
  'rack',
  'shelf',
  'bin',
  'position',
];

const getLocationIcon = (type: Location['type']) => {
  switch (type) {
    case 'site': return <Business fontSize="small" />;
    case 'kitchen': return <Restaurant fontSize="small" />;
    case 'building': return <FactoryOutlined fontSize="small" />;
    case 'floor': return <Router fontSize="small" />;
    case 'zone': return <LocationOn fontSize="small" />;
    case 'rack':
    case 'shelf':
    case 'bin':
    case 'position':
    case 'aisle': return <Storage fontSize="small" />;
    default: return <LocationOn fontSize="small" />;
  }
};

const getLocationColor = (type: Location['type']): string => {
  switch (type) {
    case 'site': return '#1565c0';
    case 'kitchen': return '#2e7d32';
    case 'building': return '#455a64';
    case 'floor': return '#e65100';
    case 'zone': return '#6a1b9a';
    case 'rack':
    case 'shelf':
    case 'bin':
    case 'position':
    case 'aisle': return '#00695c';
    default: return '#616161';
  }
};

interface LocationNodeProps {
  location: Location;
  depth: number;
  onAddChild: (parent: Location) => void;
}

const LocationNode: React.FC<LocationNodeProps> = ({ location, depth, onAddChild }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = location.children && location.children.length > 0;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          pl: depth * 3,
          py: 0.75,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
          cursor: hasChildren ? 'pointer' : 'default',
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        ) : (
          <Box width={28} />
        )}

        <Box
          sx={{
            color: getLocationColor(location.type),
            display: 'flex',
            alignItems: 'center',
            mr: 1,
          }}
        >
          {getLocationIcon(location.type)}
        </Box>

        <Box flex={1} display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={depth === 0 ? 700 : 500}>
            {location.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ({location.code})
          </Typography>
          <Chip
            label={location.type.toUpperCase()}
            size="small"
            variant="outlined"
            sx={{
              color: getLocationColor(location.type),
              borderColor: getLocationColor(location.type),
              height: 18,
              fontSize: '0.65rem',
            }}
          />
          <Chip
            label={location.status.toUpperCase()}
            color={location.status === 'active' ? 'success' : 'default'}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        </Box>

        <Button
          size="small"
          startIcon={<Add />}
          onClick={(e) => { e.stopPropagation(); onAddChild(location); }}
          sx={{ mr: 1, fontSize: '0.7rem' }}
        >
          Add Child
        </Button>
      </Box>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto">
          {location.children!.map((child) => (
            <LocationNode
              key={child.id}
              location={child}
              depth={depth + 1}
              onAddChild={onAddChild}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const LocationsTree: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { locations, locationsLoading, error } = useSelector(
    (state: RootState) => state.masterData
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [parentLocation, setParentLocation] = useState<Location | null>(null);
  const [form, setForm] = useState<Partial<Location>>({
    code: '',
    name: '',
    type: 'site',
    status: 'active',
    description: '',
    restrictions: {},
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const openAddDialog = (parent?: Location) => {
    setParentLocation(parent || null);
    setForm({
      code: '',
      name: '',
      type: parent ? getNextType(parent.type) : 'site',
      status: 'active',
      description: '',
      restrictions: {},
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const getNextType = (parentType: Location['type']): Location['type'] => {
    switch (parentType) {
      case 'site': return 'building';
      case 'kitchen': return 'zone';
      case 'building': return 'floor';
      case 'floor': return 'zone';
      case 'zone': return 'aisle';
      case 'aisle': return 'rack';
      case 'rack': return 'shelf';
      case 'shelf': return 'bin';
      default: return 'position';
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!form.code?.trim()) errors.code = 'Code is required';
    if (!form.name?.trim()) errors.name = 'Name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const restrictions = form.type === 'kitchen'
      ? {
        ...(form.restrictions || {}),
        domain: 'kitchen-ops',
        profileType: 'kitchen',
        kitchenType: form.restrictions?.kitchenType || 'satellite',
      }
      : form.restrictions;
    const createdLocation = await dispatch(
      createLocation({
        ...form,
        restrictions,
        parentId: parentLocation?.id,
        parentName: parentLocation?.name,
      })
    ).unwrap();

    if (createdLocation.type === 'kitchen') {
      await axios.post(`${API_BASE_URL}/kitchen-ops/kitchens`, {
        kitchenCode: createdLocation.code,
        kitchenName: createdLocation.name,
        kitchenType: restrictions?.kitchenType || 'satellite',
        status: createdLocation.status,
        locationId: createdLocation.id,
        locationCode: createdLocation.code,
        locationName: createdLocation.name,
        latitude: restrictions?.latitude,
        longitude: restrictions?.longitude,
        createdBy: 'frontend',
        metadata: { masterDataLocationType: 'kitchen' },
      }, { headers: authHeaders() });
    }
    setDialogOpen(false);
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Location Hierarchy</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => openAddDialog()}>
          Add Location
        </Button>
      </Box>

      <Paper sx={{ p: 2, minHeight: 400 }}>
        {locations.length === 0 && !locationsLoading && (
          <Alert severity="info">
            No locations defined. Add a location to get started.
          </Alert>
        )}
        {locations.map((location) => (
          <LocationNode
            key={location.id}
            location={location}
            depth={0}
            onAddChild={openAddDialog}
          />
        ))}
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
          Location Types:
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          {LOCATION_TYPES.map((type) => (
            <Box key={type} display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ color: getLocationColor(type), display: 'flex' }}>
                {getLocationIcon(type)}
              </Box>
              <Typography variant="caption" sx={{ color: getLocationColor(type), fontWeight: 600 }}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {parentLocation
            ? `Add Child Location under "${parentLocation.name}"`
            : 'Add New Location'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {parentLocation && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Parent: {parentLocation.code} — {parentLocation.name} ({parentLocation.type})
              </Alert>
            )}
            <Box display="flex" gap={2}>
              <TextField
                label="Code *"
                value={form.code || ''}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                error={!!formErrors.code}
                helperText={formErrors.code}
                size="small"
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Type *</InputLabel>
                <Select
                  value={form.type || 'site'}
                  label="Type *"
                  onChange={(e) => setForm({ ...form, type: e.target.value as Location['type'] })}
                >
                  {LOCATION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ color: getLocationColor(t), display: 'flex' }}>
                          {getLocationIcon(t)}
                        </Box>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Location Name *"
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              size="small"
              fullWidth
            />
            {form.type === 'kitchen' && (
              <FormControl size="small">
                <InputLabel>Kitchen Type</InputLabel>
                <Select
                  value={form.restrictions?.kitchenType || 'satellite'}
                  label="Kitchen Type"
                  onChange={(e) => setForm({
                    ...form,
                    restrictions: {
                      ...(form.restrictions || {}),
                      domain: 'kitchen-ops',
                      profileType: 'kitchen',
                      kitchenType: e.target.value,
                    },
                  })}
                >
                  <MenuItem value="central">Central Kitchen</MenuItem>
                  <MenuItem value="satellite">Satellite Kitchen</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              label="Description"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status || 'active'}
                label="Status"
                onChange={(e) => setForm({ ...form, status: e.target.value as Location['status'] })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Create Location
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationsTree;
